import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { AsyncPipe } from '@angular/common';
import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { isEqual } from 'lodash-es';
import { Observable, Subject, takeUntil } from 'rxjs';
import { LocalizePipe } from '../../pipes/standalone/localize.pipe';
import { Utils } from '../../utils/utils';
import { LocaleString } from '../../translate/localeString.model';

@Component({
    selector: 'chiplist-ac',
    templateUrl: './chiplist-ac.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [AsyncPipe, FormsModule, MatAutocompleteModule, MatChipsModule, MatFormFieldModule, MatIconModule, MatInputModule, LocalizePipe, ReactiveFormsModule, TranslocoModule]
})
export class ChiplistAutocompleteComponent implements OnInit, OnDestroy {

    @Input() control: FormControl; // required
    @Input() searchCtrl: FormControl; // required
    @Input() caption: string;
    @Input() placeholder: string;
    @Input() hint: string;
    @Input() readonly = false;
    @Input() required = false;
    @Input() options: Observable<any[]>; // required require a caption field
    @Input() id: string; // required name of the field used to check if an item exists
    @Input() chipMask: string; // mask used to render the chips
    @Input() optionMask: string; // mask used to render the options

    option: any[] = [];

    readonly separators: number[] = [ENTER, COMMA];

    private unsubscribeAll = new Subject<void>();

    constructor(private transloco: TranslocoService) { }

    ngOnInit() {
        if (!this.control.value) {
            this.control.setValue([], { emitEvent: false });
        }

        this.options
            .pipe(takeUntil(this.unsubscribeAll))
            .subscribe(options => this.option = options);
    }

    ngOnDestroy() {
        this.unsubscribeAll.next();
        this.unsubscribeAll.complete();
    }

    removeChip(index: number) {
        const value = this.control.value;
        value.splice(index, 1);
        this.control.setValue(value);
    }

    add(event: MatChipInputEvent) {
        const value = (event.value || '').trim();

        if (value) {
            const option = this.option.find(o => typeof o[this.id] === 'string' ? Utils.removeAccents(o[this.id]).toLowerCase() === Utils.removeAccents(value).toLowerCase() 
                : o[this.id] === value);
            if (option) {
                const selectedOptions = this.control.value;
                selectedOptions.push(option);
                this.control.setValue(selectedOptions);
            }
        }

        event.chipInput.clear();
        this.searchCtrl.setValue(null);
    }

    selected(event: MatAutocompleteSelectedEvent) {
        if (!this.control.value.find(v => typeof v === 'string' || typeof v === 'number' ? v === event.option.value[this.id] :
        this.id ? v[this.id] === event.option.value[this.id] : isEqual(v, event.option.value))) {
            const value = this.control.value;
            value.push(event.option.value);
            this.control.setValue(value);
        }
        this.searchCtrl.setValue('');
    }

    displayFn(option: any) {
        return option && option.caption ? option.caption : '';
    }

    getCaption(item: any, type: 'chip' | 'option') {
        const caption: LocaleString = {};
        this.transloco.getAvailableLangs().map((lang) => {
            caption[lang] = '';
        });

        let isText = true;
        const mask = type === 'chip' ? this.chipMask : this.optionMask;
        for (const part of mask.split('$')) {
            if (isText) {
                this.transloco.getAvailableLangs().map((lang) => {
                    caption[lang] = caption[lang].concat(part);
                });
            } else {
                this.transloco.getAvailableLangs().map((lang) => {
                    caption[lang] = caption[lang].concat(item[part] ? item[part][lang] ?? item[part] : '');
                });
            }
            isText = !isText;
        }
        return caption;
    }

}
